import { retainingWallsBuildUpApi } from "../api/retainingWallsBuildUp";
import { geoToberWallBuildUpApi } from "../api/wallSectionBuildUp";
import type { RetainingWallsBuildUpSpecification, RetainingWallsBuildUpItem, RetainingWallsBuildUpConfig } from "../api/retainingWallsBuildUp";
import type { GeoToberWallBuildUpSpecification, GeoToberWallBuildUpItem } from "../api/wallSectionBuildUp";
import {
  wallSpecifications,
  wallFieldToSpecId,
  WALLS_BUILD_UP_ITEM_CODE_MAP,
  isBlockworkBuildUpItem,
  getWallsBuildUpRateMultiplier,
} from "../mock/walls-section-build-up";
import {
  geoToberWallSpecifications,
  geoToberWallFieldToSpecId,
  GEO_WALL_BUILD_UP_ITEM_CODE_MAP,
  TOBERMORE_BUILD_UP_ITEM_CODE_MAP,
} from "../mock/geo-tober-wall-build-up";
import { getNumericBoqVersion } from "./boqVersionPrefix";

/**
 * Helper: look up nettRate from item library data by evaluate code.
 */
const getRateFromLibrary = (evaluateCode: string, itemLibraryData: any): number => {
  const items = itemLibraryData as unknown as any[];
  if (!Array.isArray(items) || items.length === 0) return 0;
  const match = items.find(
    (lib: any) =>
      lib.resourceCode?.trim().toUpperCase() === evaluateCode.trim().toUpperCase(),
  );
  return match?.nettRate ?? 0;
};

/**
 * Build description → mock item id map for a set of specifications.
 */
// Queue of ids per description, scoped to one spec's own item list. A plain
// last-write-wins Map collapses specs that list the same description twice
// (e.g. Tobermore TPCW's two "Stockpile Onsite" rows) onto a single id, so
// both rows sync together. Building this per-spec and consuming it in item
// order disambiguates repeated descriptions correctly.
export const buildDescriptionToIdQueue = (items: Array<{ id: string; description: string }>) => {
  const map = new Map<string, string[]>();
  items.forEach((item) => {
    const key = item.description.toLowerCase();
    const queue = map.get(key) ?? [];
    queue.push(item.id);
    map.set(key, queue);
  });
  return map;
};

interface DimensionItem {
  description: string;
  length: number;
  width: number;
  height: number;
  net: number;
  unit: string;
}

interface SavedBuildUpItem {
  id?: string;
  rate?: number;
  labour?: number;
}

export interface BuildRetainingWallSpecsFromStateParams {
  groundworksData: {
    retainingWallsTable?: Array<Record<string, number | string | null | undefined>>;
    wallsBuildUpData?: Record<string, SavedBuildUpItem[]>;
    wallsBuildUpDimensions?: Record<string, DimensionItem[]>;
  };
  itemLibraryData: unknown;
  ohpPercent: number;
  prelimsPercent?: number;
}

export interface BuildGeoWallSpecsFromStateParams {
  groundworksData: {
    geoToberWallTable?: Array<Record<string, number | string | null | undefined>>;
    geoToberWallBuildUpData?: Record<string, SavedBuildUpItem[]>;
    geoToberWallBuildUpDimensions?: Record<string, DimensionItem[]>;
    geoToberWallDimensionValues?: Record<string, string>;
  };
  itemLibraryData: unknown;
  ohpPercent: number;
  prelimsPercent?: number;
}

/**
 * Build retaining wall specs from state data without any API calls.
 * Used as a fallback when neither React Query cache nor the API has specs yet
 * (e.g. first save before saveRetainingWallsBuildUp has run).
 */
export const buildRetainingWallSpecsFromState = ({
  groundworksData,
  itemLibraryData,
  ohpPercent,
  prelimsPercent = 0,
}: BuildRetainingWallSpecsFromStateParams): RetainingWallsBuildUpSpecification[] => {
  const retainingWallsData = groundworksData.retainingWallsTable || [];
  if (retainingWallsData.length === 0) return [];

  const wallTypeTotals: Record<string, number> = {};
  Object.keys(wallFieldToSpecId).forEach((fieldName) => { wallTypeTotals[fieldName] = 0; });
  retainingWallsData.forEach((row) => {
    Object.keys(wallFieldToSpecId).forEach((fieldName) => {
      const value = row[fieldName];
      if (typeof value === "number" && !isNaN(value)) wallTypeTotals[fieldName] += value;
    });
  });

  const activeWallTypeIds = Object.entries(wallTypeTotals)
    .filter(([, total]) => total > 0)
    .map(([fieldName]) => wallFieldToSpecId[fieldName]);

  if (activeWallTypeIds.length === 0) return [];

  const savedWallsBuildUpData = groundworksData.wallsBuildUpData || {};
  const importedDimensionsRaw = groundworksData.wallsBuildUpDimensions || {};

  const specifications: RetainingWallsBuildUpSpecification[] = wallSpecifications
    .filter((spec) => activeWallTypeIds.includes(spec.id))
    .map((spec) => {
      const savedData = savedWallsBuildUpData[spec.id];
      const importedItems = importedDimensionsRaw[spec.id];
      const descriptionToMockIdQueue = buildDescriptionToIdQueue(spec.items);
      const descUsedCount = new Map<string, number>();

      const mergedItems = (importedItems && importedItems.length > 0)
        ? importedItems
            .filter((imported) => {
              const desc = String(imported.description ?? "").trim();
              return desc.length > 0 && !/^[0-9.]+$/.test(desc);
            })
            .map((imported) => {
              const descKey = imported.description.toLowerCase();
              const normalizedDescKey = descKey.replace(/\s*(ø)\s*/g, " ø ").replace(/\s+/g, " ").trim();
              const queue = descriptionToMockIdQueue.get(descKey) ?? descriptionToMockIdQueue.get(normalizedDescKey);
              const occurrence = descUsedCount.get(descKey) ?? 0;
              descUsedCount.set(descKey, occurrence + 1);
              const mockId = queue?.[occurrence];
              const id = mockId ?? descKey.replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
              const evaluateCode = WALLS_BUILD_UP_ITEM_CODE_MAP[id];
              const libraryRate = evaluateCode ? getRateFromLibrary(evaluateCode, itemLibraryData) : 0;
              const savedItem = savedData?.find((s) => s.id === id);
              // 100mm-blockwork course counts (×1–×8) and facing-brickwork
              // half-brick widths (×1–×7) re-derive `libraryRate × N` so the
              // stored rate matches the BoQ payload / Bill Summary / Sum
              // Analysis pricing — see `WALLS_BUILD_UP_RATE_MULTIPLIERS`.
              const isMultiplied = isBlockworkBuildUpItem(id);
              const rateMultiplier = getWallsBuildUpRateMultiplier(id);
              const rate = isMultiplied
                ? libraryRate * rateMultiplier
                : savedItem?.rate ? (savedItem.rate !== 0 ? savedItem.rate : libraryRate) : libraryRate;
              return {
                id,
                description: imported.description,
                length: imported.length || undefined,
                width: imported.width || undefined,
                height: imported.height || undefined,
                net: imported.net,
                unit: imported.unit,
                rate,
                labour: savedItem?.labour ?? 0,
                total: imported.net * rate,
              };
            })
        : [];

      const netTotal = mergedItems.reduce((sum, item) => sum + item.total, 0);
      const ohpAmount = netTotal * (ohpPercent / 100);
      const grandTotal = netTotal * (1 + ohpPercent / 100) * (1 + prelimsPercent / 100);
      return {
        id: spec.id,
        name: spec.name,
        type: spec.type,
        category: spec.category,
        items: mergedItems.map((item) => ({
          id: item.id,
          description: item.description,
          length: item.length,
          width: item.width,
          height: item.height,
          net: item.net ?? 0,
          unit: item.unit ?? "",
          rate: item.rate ?? 0,
          labour: item.labour ?? 0,
          total: item.total ?? 0,
        })),
        netTotal,
        ohpPercent,
        ohpAmount,
        grandTotal,
        prelimsPercent,
      };
    })
    .filter((s) => s.items.length > 0);

  return specifications;
};

/**
 * Build geo/tober wall specs from state data without any API calls.
 * Used as a fallback when neither React Query cache nor the API has specs yet.
 */
export const buildGeoToberWallSpecsFromState = ({
  groundworksData,
  itemLibraryData,
  ohpPercent,
  prelimsPercent = 0,
}: BuildGeoWallSpecsFromStateParams): GeoToberWallBuildUpSpecification[] => {
  const tableData = groundworksData.geoToberWallTable || [];
  if (tableData.length === 0) return [];

  const wallTypeTotals: Record<string, number> = {};
  Object.keys(geoToberWallFieldToSpecId).forEach((fieldName) => { wallTypeTotals[fieldName] = 0; });
  tableData.forEach((row) => {
    Object.keys(geoToberWallFieldToSpecId).forEach((fieldName) => {
      const value = row[fieldName];
      if (typeof value === "number" && !isNaN(value)) wallTypeTotals[fieldName] += value;
    });
  });

  const tpcwDimensionValues = groundworksData.geoToberWallDimensionValues || {};

  const activeSpecIds = Object.entries(wallTypeTotals)
    .filter(([, total]) => total > 0)
    .map(([fieldName]) => geoToberWallFieldToSpecId[fieldName]);

  if (activeSpecIds.length === 0) return [];

  const savedBuildUpData = groundworksData.geoToberWallBuildUpData || {};
  const importedBuildUpDimensions = groundworksData.geoToberWallBuildUpDimensions || {};

  const specifications: GeoToberWallBuildUpSpecification[] = geoToberWallSpecifications
    .filter((spec) => {
      if (!activeSpecIds.includes(spec.id)) return false;
      const tpcwMatch = spec.id.match(/^tobermore_(tpcw\d+)$/);
      if (tpcwMatch) {
        // Include if explicitly configured OR if imported build-up data exists for this spec
        return !!tpcwDimensionValues[tpcwMatch[1]] || !!(importedBuildUpDimensions[spec.id]?.length);
      }
      return true;
    })
    .map((spec) => {
      const savedData = savedBuildUpData[spec.id];
      const importedItems = importedBuildUpDimensions[spec.id];
      const codeMap = spec.type === "geowall" ? GEO_WALL_BUILD_UP_ITEM_CODE_MAP : TOBERMORE_BUILD_UP_ITEM_CODE_MAP;
      const descriptionToMockIdQueue = buildDescriptionToIdQueue(spec.items);
      const descUsedCount = new Map<string, number>();

      const mergedItems = (importedItems && importedItems.length > 0)
        ? importedItems
            .filter((imported) => {
              const desc = String(imported.description ?? "").trim();
              return desc.length > 0 && !/^[0-9.]+$/.test(desc);
            })
            .map((imported) => {
              const descKey = imported.description.toLowerCase();
              const normalizedDescKey = descKey.replace(/\s*(ø)\s*/g, " ø ").replace(/\s+/g, " ").trim();
              const queue = descriptionToMockIdQueue.get(descKey) ?? descriptionToMockIdQueue.get(normalizedDescKey);
              const occurrence = descUsedCount.get(descKey) ?? 0;
              descUsedCount.set(descKey, occurrence + 1);
              const mockId = queue?.[occurrence];
              const id = mockId ?? descKey.replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
              const evaluateCode = codeMap[id];
              const libraryRate = evaluateCode ? getRateFromLibrary(evaluateCode, itemLibraryData) : 0;
              const savedItem = savedData?.find((s) => s.id === id);
              const rate = savedItem?.rate ? (savedItem.rate !== 0 ? savedItem.rate : libraryRate) : libraryRate;
              return {
                id,
                description: imported.description,
                length: imported.length || undefined,
                width: imported.width || undefined,
                height: imported.height || undefined,
                net: imported.net,
                unit: imported.unit,
                rate,
                labour: savedItem?.labour ?? 0,
                total: imported.net * rate,
              };
            })
        : [];

      const netTotal = mergedItems.reduce((sum, item) => sum + item.total, 0);
      const ohpAmount = netTotal * (ohpPercent / 100);
      const grandTotal = netTotal * (1 + ohpPercent / 100) * (1 + prelimsPercent / 100);
      return {
        id: spec.id,
        name: spec.name,
        type: spec.type,
        category: spec.category,
        items: mergedItems.map((item) => ({
          id: item.id,
          description: item.description,
          length: item.length,
          width: item.width,
          height: item.height,
          net: item.net ?? 0,
          unit: item.unit ?? "",
          rate: item.rate ?? 0,
          labour: item.labour ?? 0,
          total: item.total ?? 0,
        })),
        netTotal,
        ohpPercent,
        ohpAmount,
        grandTotal,
        prelimsPercent,
      };
    })
    .filter((s) => s.items.length > 0);

  return specifications;
};

interface SaveWallsBuildUpParams {
  tender: any;
  itemLibraryData: any;
  ohpPercent: number;
  prelimsPercent?: number;
  versionOverride?: number;
}

/**
 * Build and POST retaining walls build-up data directly from tender state.
 * This replaces the registry/trigger pattern — no mounted component needed.
 */
export const saveRetainingWallsBuildUp = async ({
  tender,
  itemLibraryData,
  ohpPercent,
  prelimsPercent = 0,
  versionOverride,
}: SaveWallsBuildUpParams): Promise<void> => {
  const retainingWallsData = tender?.groundworks?.retainingWallsTable || [];
  if (retainingWallsData.length === 0) return;

  const boqVersionNumber: number = (() => {
    const resolved = versionOverride ?? (() => {
      const boqVersionRaw = localStorage.getItem("currentBoqVersion");
      // version may be stored as "GWKS-3"/"RS-3" (set by /version-history) —
      // extract the plain numeric part so the retaining-walls-buildup API
      // always receives a number, never the raw prefixed key.
      return boqVersionRaw ? getNumericBoqVersion(JSON.parse(boqVersionRaw)?.version ?? 1) : 1;
    })();
    return resolved || 1;
  })();

  // Calculate totals per wall type from takeoff table
  const wallTypeTotals: Record<string, number> = {};
  Object.keys(wallFieldToSpecId).forEach((fieldName) => {
    wallTypeTotals[fieldName] = 0;
  });
  retainingWallsData.forEach((row: any) => {
    Object.keys(wallFieldToSpecId).forEach((fieldName) => {
      const value = row[fieldName];
      if (typeof value === "number" && !isNaN(value)) {
        wallTypeTotals[fieldName] += value;
      }
    });
  });

  // Active spec IDs (wall types with quantity > 0)
  const activeWallTypeIds = Object.entries(wallTypeTotals)
    .filter(([, total]) => total > 0)
    .map(([fieldName]) => wallFieldToSpecId[fieldName]);

  if (activeWallTypeIds.length === 0) return;

  const savedWallsBuildUpData = tender?.groundworks?.wallsBuildUpData || {};
  const importedDimensionsRaw: Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>> =
    tender?.groundworks?.wallsBuildUpDimensions || {};

  // Fetch persisted API data as fallback for specs with no imported dimensions
  let persistedSpecsMap: Record<string, RetainingWallsBuildUpSpecification> = {};
  try {
    const persisted = await retainingWallsBuildUpApi.get(tender.projectName, boqVersionNumber);
    if (persisted?.specifications) {
      persisted.specifications.forEach((s) => { persistedSpecsMap[s.id] = s; });
    }
  } catch {
    // Non-blocking — proceed without persisted data
  }

  const specifications = wallSpecifications
    .filter((spec) => activeWallTypeIds.includes(spec.id))
    .map((spec) => {
      const savedData = savedWallsBuildUpData[spec.id];
      const importedItems = importedDimensionsRaw[spec.id];
      const descriptionToMockIdQueue = buildDescriptionToIdQueue(spec.items);
      const descUsedCount = new Map<string, number>();

      let mergedItems: RetainingWallsBuildUpItem[];

      if (importedItems && importedItems.length > 0) {
        mergedItems = importedItems
          .filter((imported) => {
            const desc = String(imported.description ?? "").trim();
            return desc.length > 0 && !/^[0-9.]+$/.test(desc);
          })
          .map((imported) => {
            const descKey = imported.description.toLowerCase();
            const normalizedDescKey = descKey.replace(/\s*(ø)\s*/g, ' ø ').replace(/\s+/g, ' ').trim();
            const occurrence = descUsedCount.get(descKey) ?? 0;
            descUsedCount.set(descKey, occurrence + 1);
            const mockId = descriptionToMockIdQueue.get(descKey)?.[occurrence] ?? descriptionToMockIdQueue.get(normalizedDescKey)?.[occurrence];
            const id = mockId ?? descKey.replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
            const evaluateCode = WALLS_BUILD_UP_ITEM_CODE_MAP[id];
            const libraryRate = evaluateCode ? getRateFromLibrary(evaluateCode, itemLibraryData) : 0;
            const savedItem = savedData?.find((s: any) => s.id === id);
            // 100mm-blockwork course counts (×1–×8) and facing-brickwork
            // half-brick widths (×1–×7) re-derive `libraryRate × N` so the
            // stored rate matches the BoQ payload / Bill Summary / Sum
            // Analysis pricing — see `WALLS_BUILD_UP_RATE_MULTIPLIERS`.
            const isMultiplied = isBlockworkBuildUpItem(id);
            const rateMultiplier = getWallsBuildUpRateMultiplier(id);
            const rate = isMultiplied
              ? libraryRate * rateMultiplier
              : savedItem?.rate ? (savedItem.rate !== 0 ? savedItem.rate : libraryRate) : libraryRate;
            return {
              id,
              description: imported.description,
              length: imported.length || undefined,
              width: imported.width || undefined,
              height: imported.height || undefined,
              net: imported.net,
              unit: imported.unit,
              rate,
              labour: savedItem?.labour ?? 0,
              total: imported.net * rate,
            };
          });
      } else if (persistedSpecsMap[spec.id]?.items?.length) {
        // No imported data — use previously persisted API items as-is
        mergedItems = persistedSpecsMap[spec.id].items;
      } else {
        mergedItems = [];
      }

      const netTotal = mergedItems.reduce((sum, item) => sum + item.total, 0);
      const ohpAmount = netTotal * (ohpPercent / 100);
      const grandTotal = netTotal * (1 + ohpPercent / 100) * (1 + prelimsPercent / 100);

      return {
        id: spec.id,
        name: spec.name,
        type: spec.type,
        category: spec.category,
        items: mergedItems.map((item) => ({
          id: item.id,
          description: item.description,
          length: item.length,
          width: item.width,
          height: item.height,
          net: item.net ?? 0,
          unit: item.unit ?? "",
          rate: item.rate ?? 0,
          labour: item.labour ?? 0,
          total: item.total ?? 0,
        })),
        netTotal,
        ohpPercent,
        ohpAmount,
        grandTotal,
        prelimsPercent,
      };
    });

  if (specifications.length === 0) return;

  const wallsBuildUpConfig = tender?.groundworks?.wallsBuildUpConfig;

  await retainingWallsBuildUpApi.post({
    tenderId: tender.id,
    tenderName: tender.projectName,
    tenderVersion: boqVersionNumber,
    specifications,
    ...(wallsBuildUpConfig ? { config: wallsBuildUpConfig } : {}),
  });
};

/**
 * Build and POST geo/tober wall build-up data directly from tender state.
 * This replaces the registry/trigger pattern — no mounted component needed.
 */
export const saveGeoToberWallBuildUp = async ({
  tender,
  itemLibraryData,
  ohpPercent,
  prelimsPercent = 0,
  versionOverride,
}: SaveWallsBuildUpParams): Promise<void> => {
  const tableData = tender?.groundworks?.geoToberWallTable || [];
  if (tableData.length === 0) return;

  const boqVersionNumber: number = (() => {
    const resolved = versionOverride ?? (() => {
      const boqVersionRaw = localStorage.getItem("currentBoqVersion");
      // version may be stored as "GWKS-3"/"RS-3" (set by /version-history) —
      // extract the plain numeric part so the geo-tober-wall-buildup API
      // always receives a number, never the raw prefixed key.
      return boqVersionRaw ? getNumericBoqVersion(JSON.parse(boqVersionRaw)?.version ?? 1) : 1;
    })();
    return resolved || 1;
  })();

  // Calculate totals per wall type from takeoff table
  const wallTypeTotals: Record<string, number> = {};
  Object.keys(geoToberWallFieldToSpecId).forEach((fieldName) => {
    wallTypeTotals[fieldName] = 0;
  });
  tableData.forEach((row: any) => {
    Object.keys(geoToberWallFieldToSpecId).forEach((fieldName) => {
      const value = row[fieldName];
      if (typeof value === "number" && !isNaN(value)) {
        wallTypeTotals[fieldName] += value;
      }
    });
  });

  const tpcwDimensionValues: Record<string, string> =
    tender?.groundworks?.geoToberWallDimensionValues || {};
  const tpcwColumnHeaders: Record<string, string> =
    tender?.groundworks?.geoToberWallColumnHeaders || {};

  // Active spec IDs (wall types with quantity > 0)
  const activeSpecIds = Object.entries(wallTypeTotals)
    .filter(([, total]) => total > 0)
    .map(([fieldName]) => geoToberWallFieldToSpecId[fieldName]);

  if (activeSpecIds.length === 0) return;

  const savedBuildUpData = tender?.groundworks?.geoToberWallBuildUpData || {};
  const importedBuildUpDimensions: Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>> =
    tender?.groundworks?.geoToberWallBuildUpDimensions || {};

  // Fetch persisted API data as fallback for specs with no imported dimensions.
  // Also read previously saved tobermoreColumnDimensions so that Tobermore specs
  // are not silently dropped when state dimension values are transiently empty
  // (e.g. after GeoToberWallSpecificationContainer hydrates with an older save
  // that had empty tobermoreColumnDimensions).
  let persistedGeoSpecsMap: Record<string, GeoToberWallBuildUpSpecification> = {};
  let persistedTpcwDimensions: Record<string, string> = {};
  try {
    const persisted = await geoToberWallBuildUpApi.get(tender.projectName, boqVersionNumber);
    if (persisted?.specifications) {
      persisted.specifications.forEach((s) => { persistedGeoSpecsMap[s.id] = s; });
    }
    if (persisted?.tobermoreColumnDimensions) {
      persistedTpcwDimensions = persisted.tobermoreColumnDimensions;
    }
  } catch {
    // Non-blocking — proceed without persisted data
  }

  // Merge: current state takes precedence; fall back to previously saved API dimensions
  const effectiveTpcwDimensionValues: Record<string, string> = {
    ...persistedTpcwDimensions,
    ...tpcwDimensionValues,
  };

  const allSpecs = geoToberWallSpecifications
    .filter((spec) => {
      if (!activeSpecIds.includes(spec.id)) return false;
      const tpcwMatch = spec.id.match(/^tobermore_(tpcw\d+)$/);
      if (tpcwMatch) {
        return !!effectiveTpcwDimensionValues[tpcwMatch[1]];
      }
      return true;
    })
    .map((spec) => {
      const savedData = savedBuildUpData[spec.id];
      const importedItems = importedBuildUpDimensions[spec.id];
      const codeMap =
        spec.type === "geowall"
          ? GEO_WALL_BUILD_UP_ITEM_CODE_MAP
          : TOBERMORE_BUILD_UP_ITEM_CODE_MAP;
      const descriptionToMockIdQueue = buildDescriptionToIdQueue(spec.items);
      const descUsedCount = new Map<string, number>();

      let mergedItems: GeoToberWallBuildUpItem[];

      if (importedItems && importedItems.length > 0) {
        mergedItems = importedItems
          .filter((imported) => {
            const desc = String(imported.description ?? "").trim();
            return desc.length > 0 && !/^[0-9.]+$/.test(desc);
          })
          .map((imported) => {
            const descKey = imported.description.toLowerCase();
            const normalizedDescKey = descKey.replace(/\s*(ø)\s*/g, ' ø ').replace(/\s+/g, ' ').trim();
            const occurrence = descUsedCount.get(descKey) ?? 0;
            descUsedCount.set(descKey, occurrence + 1);
            const mockId = descriptionToMockIdQueue.get(descKey)?.[occurrence] ?? descriptionToMockIdQueue.get(normalizedDescKey)?.[occurrence];
            const id = mockId ?? descKey.replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
            const evaluateCode = codeMap[id];
            const libraryRate = evaluateCode ? getRateFromLibrary(evaluateCode, itemLibraryData) : 0;
            const savedItem = savedData?.find((s: any) => s.id === id);
            const rate = savedItem?.rate ? (savedItem.rate !== 0 ? savedItem.rate : libraryRate) : libraryRate;
            return {
              id,
              description: imported.description,
              length: imported.length || undefined,
              width: imported.width || undefined,
              height: imported.height || undefined,
              net: imported.net,
              unit: imported.unit,
              rate,
              labour: savedItem?.labour ?? 0,
              total: imported.net * rate,
            };
          });
      } else if (persistedGeoSpecsMap[spec.id]?.items?.length) {
        // No imported data — use previously persisted API items as-is
        mergedItems = persistedGeoSpecsMap[spec.id].items;
      } else {
        mergedItems = [];
      }

      const netTotal = mergedItems.reduce((sum, item) => sum + item.total, 0);
      const ohpAmount = netTotal * (ohpPercent / 100);
      const grandTotal = netTotal * (1 + ohpPercent / 100) * (1 + prelimsPercent / 100);

      return {
        id: spec.id,
        name: spec.name,
        type: spec.type,
        category: spec.category,
        items: mergedItems.map((item) => ({
          id: item.id,
          description: item.description,
          length: item.length,
          width: item.width,
          height: item.height,
          net: item.net ?? 0,
          unit: item.unit ?? "",
          rate: item.rate ?? 0,
          labour: item.labour ?? 0,
          total: item.total ?? 0,
        })),
        netTotal,
        ohpPercent,
        ohpAmount,
        grandTotal,
        prelimsPercent,
      };
    });

  const specsToPost = allSpecs.filter((s) => s.items.length > 0);
  if (specsToPost.length === 0) return;

  await geoToberWallBuildUpApi.post({
    tenderId: tender.id,
    tenderName: tender.projectName,
    tenderVersion: boqVersionNumber,
    tobermoreColumnHeaders: tpcwColumnHeaders,
    tobermoreColumnDimensions: effectiveTpcwDimensionValues,
    specifications: specsToPost,
  });
};

/**
 * Convenience: save both wall build-ups in parallel.
 */
export const saveBothWallsBuildUp = async (params: SaveWallsBuildUpParams): Promise<void> => {
  await Promise.all([
    saveRetainingWallsBuildUp(params).catch((err) =>
      console.error("Failed to save retaining walls build-up:", err),
    ),
    saveGeoToberWallBuildUp(params).catch((err) =>
      console.error("Failed to save geo/tober wall build-up:", err),
    ),
  ]);
};

interface CopyWallsBuildUpParams {
  tenderId: string;
  tenderName: string;
  fromVersion: number;
  toVersion: number;
  currentRetainingWallsConfig?: { facingBrickworkLabel?: string };
}

/**
 * For version >= 2: fetch build-up data from the previous version and re-post
 * it for the new version. This ensures real takeoff data (not mock) is preserved
 * since wallsBuildUpDimensions is not stored in the BOQ payload.
 *
 * `currentRetainingWallsConfig` (when provided) takes precedence over the previous
 * version's persisted config, so user edits made in the current session are not
 * overwritten by the copy.
 */
export const copyWallsBuildUpFromPreviousVersion = async ({
  tenderId,
  tenderName,
  fromVersion,
  toVersion,
  currentRetainingWallsConfig,
}: CopyWallsBuildUpParams): Promise<void> => {
  await Promise.all([
    retainingWallsBuildUpApi.get(tenderName, fromVersion).then(async (data) => {
      if (data && data.specifications && data.specifications.length > 0) {
        const effectiveConfig = currentRetainingWallsConfig ?? data.config;
        await retainingWallsBuildUpApi.post({
          tenderId,
          tenderName,
          tenderVersion: toVersion,
          specifications: data.specifications as RetainingWallsBuildUpSpecification[],
          ...(effectiveConfig ? { config: effectiveConfig } : {}),
        });
      }
    }).catch((err) => console.error("Failed to copy retaining walls build-up:", err)),

    geoToberWallBuildUpApi.get(tenderName, fromVersion).then(async (data) => {
      if (data && data.specifications && data.specifications.length > 0) {
        await geoToberWallBuildUpApi.post({
          tenderId,
          tenderName,
          tenderVersion: toVersion,
          tobermoreColumnHeaders: data.tobermoreColumnHeaders ?? {},
          tobermoreColumnDimensions: data.tobermoreColumnDimensions ?? {},
          specifications: data.specifications as GeoToberWallBuildUpSpecification[],
        });
      }
    }).catch((err) => console.error("Failed to copy geo/tober wall build-up:", err)),
  ]);
};

/**
 * Merge persisted API wall build-up specifications (Retaining Walls + Geo &
 * Tobermore) into a `groundworks` object, overwriting `wallsBuildUpDimensions`
 * and `geoToberWallBuildUpDimensions`. Returns a shallow clone — the input is
 * not mutated.
 *
 * The Plot Matrix's per-spec netPerM calculation
 * (`buildRetainingWallNetPerMetreMap`, which now covers GRW/DRW/CCRW and
 * GPCW/TPCW in one map) reads from these dimension fields. If a caller of
 * `generatePlotMatrixCategoryRows` passes `state.groundworks` directly
 * without merging, the wall netPerM map ends up empty and the Plot Matrix
 * Retaining Walls Foundation column silently drops the wall amounts (BoQ
 * uses the API-persisted specs and reads non-zero, so the two diverge).
 *
 * The API response is the **single source of truth** for wall build-up
 * dimensions — local-state `wallsBuildUpDimensions` /
 * `geoToberWallBuildUpDimensions` are unconditionally overwritten, even when
 * the API returns no specs (or hasn't resolved yet). This mirrors BoQ Sum
 * Analysis (`calculateRetScreenWallsGrandTotal` /
 * `calculateGEOToberWallGrandTotal`), which only consume the persisted-specs
 * argument and have no local-state fallback. Result: Plot Matrix and BoQ Sum
 * Analysis wall totals reconcile to the penny in every state — during
 * loading they both show 0 for walls, after the API resolves they both use
 * the same API data.
 */
export const mergePersistedWallBuildUp = (
  groundworks: Record<string, unknown> | undefined | null,
  retWallsBuildUp?: { specifications?: RetainingWallsBuildUpSpecification[]; config?: RetainingWallsBuildUpConfig } | null,
  geoToberWallBuildUp?: { specifications?: GeoToberWallBuildUpSpecification[] } | null,
): Record<string, unknown> | undefined => {
  if (!groundworks) return groundworks ?? undefined;
  const merged: Record<string, unknown> = { ...groundworks };

  // Facing Brickwork / Screen Walls & EO Pillar Configure Headers choices —
  // always take the freshly-fetched API config over whatever's in local/
  // localStorage-hydrated `groundworks.wallsBuildUpConfig`, so Plot Matrix
  // and BoQ Sum Analysis reconcile with the take-off page's Configure modal
  // (which persists directly to this same endpoint) even on a hard refresh
  // that never visited /groundworks/retaining-walls in this session.
  if (retWallsBuildUp?.config) {
    merged.wallsBuildUpConfig = retWallsBuildUp.config;
  }

  const retDims: Record<string, Array<{ id: string; description: string; net: number; unit: string; rate: number }>> = {};
  retWallsBuildUp?.specifications?.forEach((spec) => {
    retDims[spec.id] = spec.items.map((item) => ({
      id: item.id,
      description: item.description,
      net: item.net,
      unit: item.unit,
      rate: item.rate,
    }));
  });
  merged.wallsBuildUpDimensions = retDims;

  const geoDims: Record<string, Array<{ id: string; description: string; net: number; unit: string; rate: number }>> = {};
  geoToberWallBuildUp?.specifications?.forEach((spec) => {
    geoDims[spec.id] = spec.items.map((item) => ({
      id: item.id,
      description: item.description,
      net: item.net,
      unit: item.unit,
      rate: item.rate,
    }));
  });
  merged.geoToberWallBuildUpDimensions = geoDims;

  return merged;
};
